import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import ChartButton from '../../../components/Button/ChartButton';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import Chart from "../../../components/Chart";
import { ClearButton } from '../../../components/Button/iconbutton';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { LFT, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';



const LFTForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [lftRecords, setLftRecords] = useState<LFT[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [editRecord, setEditRecord] = useState<LFT | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [showClearFilterButton, setShowClearFilterButton] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<LFT | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }
  useEffect(() => {
    fetchLFTRecords();
    setShowClearFilterButton(!!(searchTerm !== '' || (dateRange.startDate && dateRange.endDate)))
  }, [page, rowsPerPage, searchTerm,dateRange]);

  const fetchLFTRecords = async (startDate: string = '', endDate: string = '') => {
    setLoading(true);
    try {
      const response = await axios.get<ObjectiveResponse>(
        `${BASE_URL}/resource/vitals`,
        {
          params: {
            limit: rowsPerPage,
            page: page + 1,
            from: startDate,
            to: endDate,
            order_by: 'details->date',
            dir: 2,
            user_id: patientId,
            slug: 'spirometer',
            searchkey: searchTerm || undefined,
          },
          headers: getAxiosConfig().headers,
        }
      );

      const { vitals } = response.data.data;
      const total = response.data.data.pagination!.total;

      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: LFT[] = vitals.map((vital) => ({
            id: vital.id,
            user_id: vital.user_id,
            consult_id: vital.consult_id ?? null,
            peripheral_id: vital.peripheral_id ?? null,
            slug: vital.slug,
            details: {
                notes: vital.notes || '', // Ensure this is included
                fvc: vital.details.fvc?.value || 0, // Flattening out the structure
                pef: vital.details.pef?.value || 0,
                date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
                time: vital.details.time,
                fef25: vital.details.fef25?.value || null,
                fef50: vital.details.fef50?.value || null,
                fef75: vital.details.fef75?.value || null,
                fef2575: vital.details.fef2575?.value || null,
                fev1_fvc: vital.details.fev1_fvc || 0,
                flags: {
                    fvcFlag: vital.details.flags?.fvcFlag || 'Normal',
                    pefFlag: vital.details.flags?.pefFlag || 'Normal',
                    fev1Flag: vital.details.flags?.fev1Flag || 'Normal',
                    fev1FvcFlag: vital.details.flags?.fev1FvcFlag || 'Normal',
                    fvcFlagColor: vital.details.flags?.fvcFlagColor || 'success',
                    pefFlagColor: vital.details.flags?.pefFlagColor || 'success',
                    fev1FlagColor: vital.details.flags?.fev1FlagColor || 'success',
                },
                range_code: vital.details.range_code || '',
                spirometerFlag:vital.details.spirometerFlag // Default value if not provided
            },
            freeze: vital.freeze || 0,
            document: vital.document || null,
        }));

        setLftRecords(formattedData);
        setTotalRecords(total);
    }
    else {
      setLftRecords([]); // Reset records if no vitals found
    }
} catch (error) {
    console.error('Error fetching LFT records:', error);
    setLftRecords([]); // Reset temperatures on error
    setTotalRecords(0);
} finally {
    setLoading(false);
}
};

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateRange({ startDate: '', endDate: '' })
    setPage(0) // Reset to the first page
    setRowsPerPage(5) // Reset rows per page if needed
    fetchLFTRecords() // Fetch all records again
    setShowClearFilterButton(false) // Hide the clear button
  }


  const renderRow = (item: LFT, index: number) => {
    return (
        <TableRow key={item.id}>
            <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                {(item.details.date )}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}> {(item.details.time)}
                   
            </TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                <span style={{
                    backgroundColor: item.details.flags?.fvcFlagColor,
                    padding: '4px 8px',
                    borderRadius: '8px',
                }}>
                    {item.details.fvc} ({item.details.fvcPercent || 0}%)
                </span>
            </TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                <span style={{
                    backgroundColor: item.details.flags?.fev1FlagColor,
                    padding: '4px 8px',
                    borderRadius: '8px',
                }}>
                    {item.details.fev1} ({item.details.fev1Percent || 0}%)
                </span>
            </TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                <span style={{
                    backgroundColor: item.details.flags?.pefFlagColor,
                    padding: '4px 8px',
                    borderRadius: '8px',
                }}>
                    {item.details.pef} ({item.details.pefPercent || 0}%)
                </span>
            </TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{item.details.fev1_fvc} %</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
          <span
            style={{
              backgroundColor: item.details.range_code,
              padding: '4px 8px',
              borderRadius: '8px',
              color: '#FFFFFF'
            }}
          >
            {item.details.spirometerFlag}
          </span>
        </TableCell>
        </TableRow>
    );
};


  const chartData = {
    labels: lftRecords.map((record) =>
      record.details ? `${record.details.date} ${record.details.time}` : ''
    ),
    datasets: [
      {
        label: 'FVC (l)',
        data: lftRecords.map((record) => Number(record.details?.fvc) || 0), // Ensure it's a number
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1,
      },
      {
        label: 'FEV1 (l)',
        data: lftRecords.map((record) => Number(record.details?.fev1) || 0), // Ensure it's a number
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 1,
      },
      {
        label: 'PEF (l/s)',
        data: lftRecords.map((record) => Number(record.details?.pef) || 0), // Ensure it's a number
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderWidth: 1,
      },
    ],
  };


  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw}`,
        },
      },
    },
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId={'LFT_Start_Date'}
            endDateId={'LFT_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates)
            }}
            value={dateRange} // Pass the selected date range
          />
          
          <IconButton
            icon={<SearchIcon />}
            onClick={() => fetchLFTRecords(dateRange.startDate, dateRange.endDate)}
          />
          {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}
        </div>
        <div className="flex justify-end space-x-4">
        
        <ChartButton
          text={showChart ? 'List' : 'Chart'}
          onClick={() => setShowChart((prev) => !prev)}
        />
       
</div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
      <TableComponent
        headers={[
          'S.No',
          'Date',
          ' Time',
          'FVC(%)',
          'FEV1(%)',
          ' PEf(%)',
          'FEV1/FVC (%)',
          'Result'
        
        ]}
        data={lftRecords}
        renderRow={renderRow} error={''} />
    
        </>
      )}

<TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    </div>
  )
}


export default LFTForm
 